import { z } from "zod";

/**
 * The four classifications seeded into every environment. They cannot be
 * deleted and their `key` is never editable.
 *
 * Note the reason differs from `employment-types.ts`: no code branches on a
 * specific classification (nothing here is the equivalent of `contractor`
 * gating the billing-entity field). They are protected purely because
 * `seedRoleClassifications` re-inserts them on the next boot, so a delete would
 * appear to work and then silently undo itself. Deactivating is the real
 * retirement path.
 */
const PROTECTED_ROLE_CLASSIFICATION_KEYS = [
  "individual_contributor",
  "manager",
  "leadership",
  "officer",
] as const;

export function isProtectedRoleClassificationKey(key: string): boolean {
  return (PROTECTED_ROLE_CLASSIFICATION_KEYS as readonly string[]).includes(key);
}

/**
 * Tokens are lowercase snake_case: they are stored on
 * `person.role_classification`, on `person_history` spans, and land verbatim in
 * audit diffs, so they need to stay greppable and stable.
 */
const roleClassificationKey = z
  .string()
  .trim()
  .min(1, "Key is required")
  .max(60, "Key is too long")
  .regex(/^[a-z][a-z0-9_]*$/, "Use lowercase letters, numbers and underscores, e.g. team_lead");

/**
 * NOS's own name for this classification. NULL means no NOS counterpart, which
 * blocks the NOS write-back for anyone holding it — deliberately visible rather
 * than silently clearing what NOS has (see `resolveRoleClassificationId`).
 */
const nosName = z.string().trim().max(200, "NOS name is too long").nullable().optional();

// Default-free base. Per the zod-v4 trap this project has hit before (WI-061),
// `.partial()` still fires `.default()`s, so no field here may carry one.
const roleClassificationFieldsSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  nosName,
  isActive: z.boolean().optional(),
});

export const roleClassificationCreateSchema = roleClassificationFieldsSchema.extend({
  key: roleClassificationKey,
});

/**
 * `key` is deliberately absent: it is immutable once created, because
 * `person.role_classification`, `person_history` and every historical audit
 * diff already store it.
 */
export const roleClassificationUpdateSchema = roleClassificationFieldsSchema.partial();

export const roleClassificationReorderSchema = z.object({
  direction: z.enum(["up", "down"]),
});

export type RoleClassificationCreateInput = z.infer<typeof roleClassificationCreateSchema>;
export type RoleClassificationUpdateInput = z.infer<typeof roleClassificationUpdateSchema>;
